import React, { useState } from 'react';
import classnames from 'classnames';
import Dropdown from './Dropdown';
import Datepicker from './Datepicker';
import NumberInput from './NumberInput';
import RadioGroupInput from './RadioGroupInput';
import ScrollableCheckbox from './ScrollableCheckbox';
import MultiCheckbox from './MultiCheckbox';
import FormTitleAndDescription from './FormTitleAndDescription';
import ReCAPTCHA from 'react-google-recaptcha';
import PhoneInput from './PhoneInput';
import { useSelector } from 'react-redux';
import { selectIsRobot } from 'store/index';

const FormControl = ({
  id,
  type,
  label,
  name,
  error,
  options,
  selectPrompt,
  required,
  settings,
  formDefinitionName,
  onChildScrollToBottom,
  dropdownOptions,
  setPreferedMethod,
  setFieldTouched,
  ...props
}) => {
  let realType = null;
  if (['text', 'Short answer'].includes(type) && settings?.fieldType === 'number') {
    realType = 'number';
  } else if (['text', 'Short answer'].includes(type) && settings?.fieldType === 'date') {
    realType = 'datepicker';
  } else if (['text', 'Short answer'].includes(type) && settings?.fieldType === 'tel') {
    realType = 'phoneInput';
  } else if (['text', 'Short answer'].includes(type)) {
    realType = 'text';
  } else if (['checkbox', 'Data Consent'].includes(type)) {
    realType = 'checkbox';
  } else if (['scrollableCheckbox'].includes(type)) {
    realType = 'scrollableCheckbox';
  } else if (['select', 'Dropdown'].includes(type)) {
    realType = 'dropdown';
  } else if (['textarea', 'Long answer'].includes(type)) {
    realType = 'textarea';
  } else if (['datepicker', 'Date'].includes(type)) {
    realType = 'datepicker';
  } else if (['number'].includes(type)) {
    realType = 'number';
  } else if (['Hidden'].includes(type)) {
    realType = 'hidden';
  } else if (['Single choice'].includes(type)) {
    realType = 'radio';
  } else if (['Multiple choice'].includes(type)) {
    realType = 'multicheckbox';
  } else if (['Title and description'].includes(type)) {
    realType = 'titleAndDescription';
  } else if (['reCAPTCHA v3 with score', 'Recaptcha2'].includes(type)) {
    realType = 'recaptcha';
  }

  const [dateValue, setDateValue] = useState(props.value);

  const handleDate = (value, fieldName) => {
    value = value.replace(/\D/g, '').substring(0, 8);

    const LENGTH = value.length;
    const DAY = value.substring(0, 2);
    const MONTH = value.substring(2, 4);
    const YEAR = value.substring(4, 8);
    const CURRENT_YEAR = new Date().getFullYear();

    if (LENGTH > 2) {
      value = value.slice(0, 2) + '/' + value.slice(2);
    }
    if (LENGTH > 4) {
      value = value.slice(0, 5) + '/' + value.slice(5);
    }

    if (DAY === '00') {
      value = value.replaceAll('00', '01');
    }

    if (Number(DAY) > 31) {
      value = value.replaceAll(DAY, '31');
    }

    if (MONTH === '00') {
      value = value.replaceAll('00', '01');
    }

    if (Number(MONTH) > 12) {
      value = value.replaceAll(MONTH, '12');
    }

    if (value.length === 10 && Number(YEAR) < 1900) {
      value = value.replaceAll(YEAR, '1900');
    }

    if (Number(YEAR) > CURRENT_YEAR) {
      value = value.replaceAll(YEAR, CURRENT_YEAR.toString());
    }

    setDateValue(value);

    if (props.onChange) {
      props.onChange({ target: { name: fieldName, value } });
    }
  };

  const handleScrollToBottom = (isBottom) => {
    if (isBottom) {
      onChildScrollToBottom(isBottom);
    }
  };

  const isRobot = useSelector(selectIsRobot);

  let input = null;
  if (['text'].includes(realType)) {
    input =
      name === 'dateOfBirth' && realType === 'text' ? (
        <input
          id={id}
          type={realType}
          className="input"
          name={name}
          aria-label={props.placeholder}
          aria-required={required}
          {...props}
          onChange={(event) => handleDate(event.target.value, name)}
          value={dateValue ? dateValue : props.value || ''}
        />
      ) : (
        <input
          id={id}
          type={realType}
          className="input"
          name={name}
          aria-label={props.placeholder}
          aria-required={required}
          {...props}
        />
      );
  } else if (realType === 'dropdown') {
    input = (
      <Dropdown
        id={name}
        className="input dropdown"
        name={name}
        options={options}
        displayEmpty={!required}
        required={required}
        {...props}
        placeholder={selectPrompt || props.placeholder}
        setPreferedMethod={setPreferedMethod}
        dropdownOptions={dropdownOptions}
      />
    );
  } else if (realType === 'textarea') {
    input = (
      <textarea id={name} type={realType} className="input" name={name} rows={settings?.numberOfRows} {...props} />
    );
  } else if (realType === 'datepicker') {
    input = (
      <Datepicker
        id={name}
        className="input"
        name={name}
        placeholderText={props.placeholder}
        required={required}
        {...props}
        maxDate={settings?.maxDate || props.maxDate}
        yearMonthSelector={settings?.yearMonthSelector}
        setFieldTouched={setFieldTouched}
      />
    );
  } else if (realType === 'number') {
    input = (
      <NumberInput
        id={name}
        className="input"
        type={realType}
        name={name}
        defaultValue={settings?.defaultValue !== '' ? settings?.defaultValue : 0}
        {...props}
      />
    );
  } else if (realType === 'hidden') {
    input = <input id={name} type={realType} className="input" name={name} {...props} />;
  } else if (realType === 'radio') {
    input = (
      <RadioGroupInput
        options={options}
        required={required}
        settings={settings}
        name={name}
        setPreferedMethod={setPreferedMethod}
        {...props}
      />
    );
  } else if (realType === 'scrollableCheckbox') {
    input = (
      <ScrollableCheckbox
        label={label}
        required={required}
        settings={settings}
        name={name}
        onScrollToBottom={handleScrollToBottom}
        {...props}
      />
    );
  } else if (realType === 'multicheckbox') {
    input = <MultiCheckbox options={options} required={required} settings={settings} name={name} {...props} />;
  } else if (realType === 'titleAndDescription') {
    input = (
      <FormTitleAndDescription settings={settings} name={name} formDefinitionName={formDefinitionName} {...props} />
    );
  } else if (realType === 'recaptcha') {
    input = !isRobot ? (
      <ReCAPTCHA
        className="recaptcha"
        sitekey={process.env.RECAPTCHA_SITE_KEY}
        {...props}
        onChange={(value) => props.onChange({ target: { name, value: value } })}
      />
    ) : null;
  } else if (realType === 'phoneInput') {
    input = <PhoneInput id={name} className="input" name={name} {...props} />;
  }

  const labelElement = label && realType !== 'hidden' && (
    <label
      htmlFor={realType === 'checkbox' ? id : name}
      className="label"
      dangerouslySetInnerHTML={{ __html: label }}
    />
  );

  let inputElement;

  if (realType) {
    if (realType === 'checkbox') {
      inputElement = (
        // <div className="checkbox">
        //   <input id={id} type={realType} className="input" name={name} {...props} />
        //   <span className="checkmark" />
        //   <label htmlFor={id} className="label" dangerouslySetInnerHTML={{ __html: label || props.placeholder }} />
        // </div>
        <ScrollableCheckbox
          label={label}
          required={required}
          settings={settings}
          name={name}
          onScrollToBottom={handleScrollToBottom}
          {...props}
        />
      );
    } else if (realType === 'recaptcha' || realType === 'scrollableCheckbox') {
      inputElement = input;
    } else {
      inputElement = (
        <>
          {labelElement} {input}
        </>
      );
    }
  }

  return (
    <>
      <div className={classnames('input-group', `field-${name}`, { error })}>
        {inputElement || (
          <div>
            Input type <b>{type}</b> not implemented
          </div>
        )}
        {error && <div className="error-message">{error}</div>}
      </div>
    </>
  );
};

export default FormControl;
